import { Button, Menu, MenuItem, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FeatureFlag, useFeatureFlag } from '@src/config/featureFlags';
import { COLORS } from '@src/constants/design-tokens/colors';
import { RouterPath } from '@src/constants/router_path';
import { useLogout } from '@src/hooks/useLogout';
import type { AuthUserState as User } from '@src/store/slices/auth';
import type React from 'react';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface UserMenuProps {
  user: User | null;
  isFocusMode: boolean;
}

export const UserMenu = memo(({ user, isFocusMode }: UserMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useLogout();
  const isAuthEnabled = useFeatureFlag(FeatureFlag.AUTH_ENABLED);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  if (!isAuthEnabled) {
    return null;
  }

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    navigate(RouterPath.profile);
    setAnchorEl(null);
  };

  const handleLogin = () => {
    navigate(RouterPath.login);
  };

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
  };

  if (user) {
    return (
      <>
        <Button
          onClick={handleProfileMenuOpen}
          color="inherit"
          size="small"
          sx={{
            fontSize: '0.85rem',
            padding: '4px 12px',
            minWidth: 'auto',
            whiteSpace: 'nowrap',
            marginRight: 1,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {user.name}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleProfileMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItem onClick={handleProfile}>
            <Typography>{t('navigation.settings')}</Typography>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <Typography>{t('navigation.logout')}</Typography>
          </MenuItem>
        </Menu>
      </>
    );
  }

  return (
    <Button
      variant="outlined"
      color="inherit"
      onClick={handleLogin}
      size="small"
      sx={(theme) => {
        const primaryColor = theme.palette.primary.main;
        const surfaceColor = isFocusMode
          ? COLORS.focusModeNav.button.surface
          : alpha(primaryColor, 0.08);
        const borderColor = isFocusMode
          ? COLORS.focusModeNav.button.border
          : alpha(primaryColor, 0.35);
        const hoverSurface = isFocusMode
          ? COLORS.focusModeNav.button.hoverSurface
          : alpha(primaryColor, 0.18);
        const activeSurface = isFocusMode
          ? COLORS.focusModeNav.button.activeSurface
          : alpha(primaryColor, 0.12);

        return {
          fontSize: '0.8rem',
          padding: '6px 18px',
          minWidth: 'auto',
          whiteSpace: 'nowrap',
          marginRight: 1,
          fontWeight: 600,
          borderRadius: '999px',
          color: isFocusMode ? COLORS.focusModeNav.indicator.text : primaryColor,
          borderColor,
          backgroundColor: surfaceColor,
          boxShadow: isFocusMode
            ? '0 4px 12px rgba(0, 0, 0, 0.25)'
            : '0 8px 18px rgba(15, 23, 42, 0.12)',
          transition: 'all 0.24s ease',
          backdropFilter: isFocusMode ? 'blur(10px)' : 'none',
          '&:hover': {
            backgroundColor: hoverSurface,
            borderColor: isFocusMode
              ? COLORS.focusModeNav.button.hoverBorder
              : alpha(primaryColor, 0.55),
            boxShadow: isFocusMode
              ? '0 6px 18px rgba(0, 0, 0, 0.28)'
              : '0 10px 22px rgba(15, 23, 42, 0.18)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            backgroundColor: activeSurface,
            transform: 'translateY(0)',
            boxShadow: isFocusMode
              ? '0 3px 10px rgba(0, 0, 0, 0.24)'
              : '0 6px 14px rgba(15, 23, 42, 0.16)',
          },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: `0 0 0 3px ${alpha(primaryColor, isFocusMode ? 0.35 : 0.25)}`,
          },
        };
      }}
    >
      {t('navigation.login')}
    </Button>
  );
});
